/** Card ids are prefixed so no queue request id can ever match a transfer. */
const CARD_PREFIX = 'transfer:';
/** The sender's own words when they gave none (InterpreterQueueService.transferCall). */
const DEFAULT_REASON = 'In-call transfer';
/** How long after a tap a refusal still belongs to that tap. */
export const TRANSFER_ACTION_WINDOW_MS = 30000;
/**
 * Refusals that mean the offer is gone for good: taken, withdrawn, the call
 * ended or moved to someone else, or this interpreter no longer fits it
 * (vrs-server/ws/call-management-handlers.js). Anything else (a dropped
 * socket, a server error) leaves the card so the interpreter can try again.
 */
export const TRANSFER_TERMINAL_ERROR_CODES = new Set([
    'CALL_ENDED',
    'NOT_FOUND',
    'SERVICE_REGION_NOT_COVERED',
    'TRANSFER_ALREADY_ACCEPTED',
    'TRANSFER_NOT_OFFERED',
    'TRANSFER_NOT_YOURS',
    'TRANSFER_STALE',
    'TRANSFER_TARGET_DEMO_ONLY',
    'TRANSFER_TARGET_LANGUAGE_MISMATCH',
    'TRANSFER_TARGET_NOT_FOUND',
    'TRANSFER_TARGET_SERVICE_MODE_MISMATCH'
]);
export function transferCardId(transferId) {
    return `${CARD_PREFIX}${transferId}`;
}
export function isTransferCard(request) {
    return Boolean(request?.transfer?.transferId);
}
function text(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
/** The offer card for a `call_transfer_offer`, or null for a malformed one. */
export function transferOfferToRequest(payload, now = Date.now()) {
    const transferId = text(payload?.transferId);
    if (!transferId) {
        return null;
    }
    const callType = payload.callType === 'vri' ? 'vri' : 'vrs';
    const reason = text(payload.reason);
    return {
        callType,
        clientName: text(payload.clientName) || 'Client',
        id: transferCardId(transferId),
        // The call's language as the server matched it; an older server sent
        // none, and then nothing is said rather than a guess (offerTitle).
        language: text(payload.language) || '',
        offerExpiresAt: text(payload.expiresAt),
        roomName: text(payload.roomName),
        timestamp: now,
        transfer: {
            callId: text(payload.callId),
            fromInterpreterName: text(payload.fromInterpreterName),
            reason: reason && reason !== DEFAULT_REASON ? reason : undefined,
            transferId
        }
    };
}
/** What Home says when `call_transfer_offer_ended` takes a card down. */
export function transferOfferEndedText(reason) {
    return reason === 'withdrawn' ? 'Transfer withdrawn.' : reason === 'expired' ? 'Transfer expired.' : 'Call ended.';
}
/** The sender's line once a colleague took the call: "Sam is taking over." */
export function transferTakingOverText(name) {
    return `${name?.trim() || 'Your colleague'} is taking over.`;
}
/** Both interpreters, when a transfer's phone leg stayed with the sender. */
export const TRANSFER_PHONE_LEG_FAILED_TEXT = 'Phone didn\u2019t transfer.';
/** Who is handing the call over: "From Sam". */
export function transferOfferLine(transfer) {
    return `From ${transfer.fromInterpreterName || 'a colleague'}`;
}
/**
 * Transfer refusals carry no transferId or operation. One arriving within
 * TRANSFER_ACTION_WINDOW_MS of a tap, with a code that means the offer is
 * gone, belongs to that tap's card.
 */
export function cardEndedByError(action, error, now = Date.now()) {
    if (!action || !error || error.operation || now - action.at > TRANSFER_ACTION_WINDOW_MS) {
        return null;
    }
    return typeof error.code === 'string' && TRANSFER_TERMINAL_ERROR_CODES.has(error.code) ? action.cardId : null;
}
// The last transfer tap, per app process. Module state rather than Redux: it
// only correlates the server's next refusal, and sign-out forgets it.
let lastAction = null;
export function noteTransferAction(action) {
    lastAction = action;
}
export function lastTransferAction() {
    return lastAction;
}
/** Forget the tap once its card is gone (or, with no id, at sign-out). */
export function forgetTransferAction(cardId) {
    if (!cardId || lastAction?.cardId === cardId) {
        lastAction = null;
    }
}
